import {
    CreateQualificationLevelEnrollmentRequest,
    CreateStudentModuleEnrollmentRequest,
    CreateTeachingGroupEnrollmentRequest,
    EnrollmentStatus,
    EnrollmentSyncRequest,
    EnrollmentType,
    QualificationLevelEnrollmentAPIModel,
    StudentModuleEnrollmentSyncModel,
    TeachingGroupEnrollmentSyncModel,
    UpdateQualificationLevelEnrollmentRequest,
    UpdateStudentModuleEnrollmentRequest,
} from "../api/APIClient";
import { assessmentEnrollmentClient } from "../api/clients";
import { unwrap, unwrapVoid } from "../api/errors";

/**
 * Who is taking what.
 *
 * Two things about this slice are easy to get wrong.
 *
 * 1. Reads use the paged `...ForSync` endpoints, not the plain `list...` ones. The plain endpoints
 *    are unbounded and documented as returning every row in the organisation - fine for a small
 *    tenant, a page-killer for a real school. The sync models are also flatter: they render the
 *    Enumeration fields as plain strings instead of `{value, name, displayName}` objects.
 *
 * 2. Enumeration values on the write side are NOT strings. `EnrollmentStatus` and `EnrollmentType`
 *    are ScadsysCore Enumeration types persisted by `.Name`, so a write has to send an object.
 *    `status()` and `enrollmentType()` build them, so no screen has to know that.
 *
 * There is no student entity in this database. A "student" is an identity-server user id, which is
 * why these models carry a bare `studentID` and no name.
 */

export type { StudentModuleEnrollmentSyncModel, TeachingGroupEnrollmentSyncModel, QualificationLevelEnrollmentAPIModel };

/** The EnrollmentStatus values the backend defines. Calculation only considers Enrolled and Completed. */
export const ENROLLMENT_STATUSES = ["Applied", "Enrolled", "Completed", "Inactive", "Archive"] as const;
export type EnrollmentStatusName = (typeof ENROLLMENT_STATUSES)[number];

export const ENROLLMENT_TYPES = ["Student", "Educator"] as const;
export type EnrollmentTypeName = (typeof ENROLLMENT_TYPES)[number];

/** Build the Enumeration payload the API expects from a plain name. */
export const status = (name: string): EnrollmentStatus => new EnrollmentStatus({ name });

export const enrollmentType = (name: string): EnrollmentType => new EnrollmentType({ name });

/** Read a name back off an Enumeration that may arrive as an object or (from sync models) a string. */
export function enumerationName(value: unknown): string {
    if (!value) return "";
    if (typeof value === "string") return value;
    return (value as { name?: string }).name ?? "";
}

const DEFAULT_PAGE = 200;

// ------------------------------------------------- student module enrollments

export interface EnrollmentPage {
    skip: number;
    take: number;
}

export const listStudentModuleEnrollments = ({ skip = 0, take = DEFAULT_PAGE }: Partial<EnrollmentPage> = {}): Promise<
    StudentModuleEnrollmentSyncModel[]
> =>
    assessmentEnrollmentClient
        .listStudentModuleEnrollmentsForSync(new EnrollmentSyncRequest({ skip, take }))
        .then((r) => unwrap(r) ?? []);

export const createStudentModuleEnrollment = (input: {
    studentID: string;
    qualificationModuleID: string;
    enrollmentStatus: string;
}): Promise<void> =>
    assessmentEnrollmentClient
        .createStudentModuleEnrollment(
            new CreateStudentModuleEnrollmentRequest({
                studentID: input.studentID,
                qualificationModuleID: input.qualificationModuleID,
                enrollmentStatus: status(input.enrollmentStatus),
            })
        )
        .then(unwrapVoid);

/** Only the status is updatable - the API does not move an enrollment to a different module. */
export const updateStudentModuleEnrollment = (id: string, enrollmentStatus: string): Promise<void> =>
    assessmentEnrollmentClient
        .updateStudentModuleEnrollment(new UpdateStudentModuleEnrollmentRequest({ id, enrollmentStatus: status(enrollmentStatus) }))
        .then(unwrapVoid);

export const deleteStudentModuleEnrollment = (id: string): Promise<void> =>
    assessmentEnrollmentClient.deleteStudentModuleEnrollment(id).then(unwrapVoid);

// --------------------------------------------- qualification level enrollments

/**
 * No paged variant exists for this one, so it uses the plain list. It is per-cohort rather than
 * per-subject, so it is an order of magnitude smaller than module enrollment.
 */
export const listQualificationLevelEnrollments = (): Promise<QualificationLevelEnrollmentAPIModel[]> =>
    assessmentEnrollmentClient.listQualificationLevelEnrollments().then((r) => unwrap(r) ?? []);

export const createQualificationLevelEnrollment = (input: {
    studentID: string;
    qualificationProgramLevelID: string;
    enrollmentStatus: string;
}): Promise<void> =>
    assessmentEnrollmentClient
        .createQualificationLevelEnrollment(
            new CreateQualificationLevelEnrollmentRequest({
                studentID: input.studentID,
                qualificationProgramLevelID: input.qualificationProgramLevelID,
                enrollmentStatus: status(input.enrollmentStatus),
            })
        )
        .then(unwrapVoid);

export const updateQualificationLevelEnrollment = (id: string, enrollmentStatus: string): Promise<void> =>
    assessmentEnrollmentClient
        .updateQualificationLevelEnrollment(
            new UpdateQualificationLevelEnrollmentRequest({ id, enrollmentStatus: status(enrollmentStatus) })
        )
        .then(unwrapVoid);

export const deleteQualificationLevelEnrollment = (id: string): Promise<void> =>
    assessmentEnrollmentClient.deleteQualificationLevelEnrollment(id).then(unwrapVoid);

// -------------------------------------------------- teaching group enrollments

export const listTeachingGroupEnrollments = ({ skip = 0, take = DEFAULT_PAGE }: Partial<EnrollmentPage> = {}): Promise<
    TeachingGroupEnrollmentSyncModel[]
> =>
    assessmentEnrollmentClient
        .listTeachingGroupEnrollmentsForSync(new EnrollmentSyncRequest({ skip, take }))
        .then((r) => unwrap(r) ?? []);

export const createTeachingGroupEnrollment = (input: {
    studentID: string;
    teachingGroupID: string;
    enrollmentType: string;
}): Promise<void> =>
    assessmentEnrollmentClient
        .createTeachingGroupEnrollment(
            new CreateTeachingGroupEnrollmentRequest({
                studentID: input.studentID,
                teachingGroupID: input.teachingGroupID,
                enrollmentType: enrollmentType(input.enrollmentType),
            })
        )
        .then(unwrapVoid);

/**
 * There is no update for a teaching group enrollment - the controller action is commented out
 * server-side. Changing someone's role in a group means deleting and re-adding.
 */
export const deleteTeachingGroupEnrollment = (id: string): Promise<void> =>
    assessmentEnrollmentClient.deleteTeachingGroupEnrollment(id).then(unwrapVoid);
